import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Chip, Grid, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { T } from 'components/common/text';
import cx from 'classnames';
import { color } from 'styles/utils';
import VisibleEyes from 'components/common/icon/VisibleEes';
import { useShallowAppSelector } from 'store/hooks';
import { invalidFormType, pageUrl } from 'lib/mapper';
import CustomText from 'components/common/text/CustomText';
import CustomSpan from 'components/common/text/CustomSpan';
import { useHistory } from 'react-router-dom';
import useInput from 'lib/hooks/useInput';
import AuthLayout from './AuthLayout';

interface SignInProps {
  isValidForm: boolean;
  nickname: {
    value: string;
    setValue: React.Dispatch<any>;
    onChange: (e: any) => void;
  };
  email: {
    value: string;
    setValue: React.Dispatch<any>;
    onChange: (e: any) => void;
  };
  password: {
    value: string;
    setValue: React.Dispatch<any>;
    onChange: (e: any) => void;
  };
  onSubmit: () => void;
  onChangeLanguage: () => void;
}

function SignIn({
  isValidForm,
  nickname,
  email,
  password,
  onSubmit,
  onChangeLanguage,
}: SignInProps) {
  const {
    recentLogin: recentLoginList,
    loginHistory,
    signInByEmployeeError,
  }: {
    recentLogin: string[];
    loginHistory: string[];
    signInByEmployeeError: { status: number; message?: string };
  } = useShallowAppSelector(state => ({
    recentLogin: state.auth.recentLogin,
    loginHistory: state.auth.loginHistory,
    signInByEmployeeError: state.employee.signInByEmployee.error,
  }));
  const history = useHistory();
  const emailList = useInput('');
  const [isShowPassword, setIsShowPassword] = useState(false);
  const isInvalidGroupCode = signInByEmployeeError?.message === invalidFormType.invalidGroupCode;
  const isInvalidEmail = signInByEmployeeError?.message === invalidFormType.invalidEmail;
  const isInvalidPassword = signInByEmployeeError?.message === invalidFormType.invalidPassword;
  const [insertShow, setInsertShow] = useState(false);

  // NOTE: 개발용 아이디 비밀번호, MUI AutoComplete사용으로 변경 불가
  const devInsertAccount = (name: string) => {
    email.setValue(`${name}@doflab.com`);
    password.setValue('dof0070!');
  };

  const handleNavigatePasswordReset = () => {
    history.push({ pathname: pageUrl.auth.passwordInquiry, state: { nickname: nickname.value } });
  };

  useEffect(() => {
    console.log('loginHistory : ', loginHistory);
    // console.log('loginHistory email : ', loginHistory[0]);
    // console.log('loginHistory nickname : ', loginHistory[1]);
    if (loginHistory.length > 0) nickname.setValue(loginHistory[1]);
  }, []);

  return (
    <AuthLayout
      // containerWidth={}
      devContent={
        <div className="dev__insert">
          {insertShow && (
            <>
              <MuiButton
                variant="outlined"
                disableElevation
                config={{ color: '#ddd', fontColor: '#777' }}
                onClick={() => devInsertAccount('sender')}
              >
                Sender Login
              </MuiButton>
              <MuiButton
                variant="outlined"
                disableElevation
                config={{ color: '#ddd', fontColor: '#777' }}
                onClick={() => devInsertAccount('receiver')}
              >
                Receiver Login
              </MuiButton>
            </>
          )}

          {/* <MuiButton
            variant="outlined"
            disableElevation
            config={{ color: '#ddd', fontColor: '#777' }}
            onClick={onChangeLanguage}
          >
            Language
          </MuiButton> */}
        </div>
      }
      title={<T>GLOBAL_LOGIN</T>}
      buttonText={<T>GLOBAL_LOGIN</T>}
      linkContent={
        <div className="authLayout__link_row">
          <CustomSpan fontColor={color.gray_font}>
            <T>SIGNIN_FORGET_PASSWORD</T>
          </CustomSpan>
          <button onClick={handleNavigatePasswordReset}>
            <CustomSpan fontWeight={500}>
              <T>GLOBAL_RESET</T>
            </CustomSpan>
          </button>
        </div>
      }
      isValidForm={isValidForm}
      onSubmit={onSubmit}
    >
      <Grid item xs={12} className="authLayout__form_row">
        <MuiWrapper>
          <TextField
            id="nickname"
            name="nickname"
            placeholder="groupCode"
            inputProps={{
              'aria-label': 'nickname',
            }}
            variant="outlined"
            fullWidth
            autoComplete="off"
            error={isInvalidGroupCode}
            value={nickname.value}
            onChange={nickname.onChange}
          />
        </MuiWrapper>
        {isInvalidGroupCode && (
          <CustomText className="form__error_text">
            <T>REGISTER_NOT_VALID</T>
          </CustomText>
        )}
      </Grid>

      <Grid item xs={12} className="authLayout__form_row">
        <Autocomplete
          freeSolo
          id="emailAuto"
          onChange={(e, newVal) => emailList.setValue(newVal)}
          // inputValue={email.value}
          onInputChange={(e, newInputVal) => {
            email.setValue(newInputVal);
          }}
          defaultValue={loginHistory[0]}
          options={recentLoginList?.map((option: any) => option)}
          renderInput={params => (
            <TextField
              {...params}
              id="email"
              name="email"
              placeholder="email"
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={email.value}
              onChange={email.onChange}
            />
          )}
        />
        {isInvalidEmail && (
          <CustomText className="form__error_text">
            <T>REGISTER_NOT_VALID</T>
          </CustomText>
        )}
      </Grid>

      <Grid item xs={12} className="authLayout__form_row">
        <MuiWrapper>
          <TextField
            id="password"
            name="password"
            placeholder="password"
            variant="outlined"
            fullWidth
            autoComplete="off"
            inputProps={{
              maxLength: 20,
            }}
            error={isInvalidPassword}
            value={password.value}
            onChange={password.onChange}
            onKeyUp={e => e.key === 'Enter' && onSubmit()}
            type={isShowPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    className={cx({ active: isShowPassword })}
                    onClick={() => setIsShowPassword(!isShowPassword)}
                  >
                    <VisibleEyes show={isShowPassword} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </MuiWrapper>
        {isInvalidPassword && (
          <CustomText className="form__error_text">
            <T>REGISTER_NOT_VALID</T>
          </CustomText>
        )}
      </Grid>
    </AuthLayout>
  );
}

export default React.memo(SignIn);
